import React, { useEffect, useRef, useState } from "react";
import { api, formatMoney, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Search, X, Plus, Trash2, Save, PhoneCall } from "lucide-react";

// Manual order entry for admins (e.g. orders placed by phone).
// Prices are computed server-side with the selected customer's pricing;
// an optional per-line EUR override covers phone-agreed special prices.
export default function AdminNewOrder({ onCreated, onClose }) {
  const [customers, setCustomers] = useState([]);
  const [userId, setUserId] = useState("");
  const [custQ, setCustQ] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lines, setLines] = useState([]); // [{product, quantity, override}]
  const [vin, setVin] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [orderDate, setOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);
  const custBoxRef = useRef(null);

  useEffect(() => {
    api.get("/admin/customers").then((r) => {
      const list = [...r.data].sort((a, b) =>
        (a.company || a.name || "").localeCompare(b.company || b.name || "", "hu"));
      setCustomers(list);
    });
  }, []);

  // Close the customer dropdown when clicking outside
  useEffect(() => {
    const onDown = (e) => {
      if (custBoxRef.current && !custBoxRef.current.contains(e.target)) setCustOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const selectedCustomer = customers.find((c) => c.id === userId) || null;
  const custNeedle = custQ.trim().toLowerCase();
  const custMatches = custNeedle
    ? customers.filter((c) =>
        (c.company || "").toLowerCase().includes(custNeedle) ||
        (c.name || "").toLowerCase().includes(custNeedle) ||
        (c.email || "").toLowerCase().includes(custNeedle) ||
        (c.tax_id || "").toLowerCase().includes(custNeedle)
      ).slice(0, 10)
    : customers.slice(0, 10);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) { seq.current++; setResults([]); setSearching(false); return; }
    const id = setTimeout(async () => {
      const mySeq = ++seq.current;
      setSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q: needle, limit: 8 } });
        if (mySeq !== seq.current) return;
        setResults(data.items || []);
      } catch {
        if (mySeq === seq.current) setResults([]);
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const addLine = (p) => {
    if (lines.some((l) => l.product.id === p.id)) { toast.info(`${p.sku} már a listában van`); return; }
    if (p.sold) { toast.error(`${p.sku} már elkelt (Eladva)`); return; }
    setLines((arr) => [...arr, { product: p, quantity: 1, override: "" }]);
    setQ("");
    setResults([]);
  };

  const setLine = (i, patch) =>
    setLines((arr) => arr.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!userId) { toast.error("Válassz vevőt a rendeléshez."); return; }
    if (lines.length === 0) { toast.error("Adj hozzá legalább egy tételt."); return; }
    for (const l of lines) {
      if (l.override !== "" && (!Number(l.override) || Number(l.override) <= 0)) {
        toast.error(`Érvénytelen egyedi ár: ${l.product.sku}`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/orders", {
        user_id: userId,
        items: lines.map((l) => ({
          product_id: l.product.id,
          quantity: l.quantity,
          unit_price: l.override !== "" ? Number(l.override) : null,
        })),
        po_number: vin,
        notes,
        send_email: sendEmail,
        created_at: orderDate || null,
      });
      if (data.email_sent) {
        toast.success(`Rendelés rögzítve · Visszaigazoló e-mail: ${(data.email_recipients || []).join(", ")}`);
      } else {
        toast.success("Rendelés rögzítve");
      }
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-elev mb-5" style={{ overflow: "visible" }} data-testid="admin-new-order-panel">
      <div className="section-title flex items-center justify-between" style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}>
        <span className="flex items-center gap-2"><PhoneCall size={14} /> Új rendelés felvitele (pl. telefonos rendelés)</span>
        <button onClick={onClose} style={{ color: "#fff" }} data-testid="new-order-close"><X size={14} /></button>
      </div>
      <div className="p-4 bg-white space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative" ref={custBoxRef}>
            <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Vevő *</label>
            {selectedCustomer ? (
              <div
                className="flex items-center justify-between gap-2 border rounded px-3 py-2"
                style={{ borderColor: "var(--brand)", background: "#F3F7FB" }}
                data-testid="new-order-customer-selected"
              >
                <div className="min-w-0">
                  <span className="font-bold text-sm">{selectedCustomer.company || selectedCustomer.name}</span>
                  <span className="text-xs ml-2 font-mono" style={{ color: "var(--text-mute)" }}>{selectedCustomer.email}</span>
                </div>
                <button
                  onClick={() => { setUserId(""); setCustQ(""); }}
                  style={{ color: "var(--text-mute)" }}
                  title="Másik vevő választása"
                  data-testid="new-order-customer-clear"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border rounded px-3 py-2" style={{ borderColor: "var(--border-hi)" }}>
                  <Search size={14} style={{ color: "var(--text-mute)" }} />
                  <input
                    className="flex-1 outline-none text-sm bg-transparent"
                    placeholder="Cégnév, név, e-mail vagy adószám..."
                    value={custQ}
                    onChange={(e) => { setCustQ(e.target.value); setCustOpen(true); }}
                    onFocus={() => setCustOpen(true)}
                    data-testid="new-order-customer-search"
                  />
                  {custQ && <button onClick={() => setCustQ("")} style={{ color: "var(--text-mute)" }}><X size={13} /></button>}
                </div>
                {custOpen && (
                  <div
                    className="absolute left-0 right-0 z-40 bg-white border shadow-lg max-h-[280px] overflow-y-auto"
                    style={{ borderColor: "var(--border-hi)" }}
                    data-testid="new-order-customer-results"
                  >
                    {custMatches.length === 0 ? (
                      <div className="p-3 text-sm text-center" style={{ color: "var(--text-mute)" }}>Nincs találat.</div>
                    ) : (
                      custMatches.map((c) => (
                        <button
                          key={c.id}
                          className="w-full text-left px-3 py-2 border-b hover:bg-slate-50 block"
                          style={{ borderColor: "var(--border)" }}
                          onClick={() => { setUserId(c.id); setCustOpen(false); }}
                          data-testid={`new-order-customer-pick-${c.email}`}
                        >
                          <span className="font-bold text-sm">{c.company || c.name}</span>
                          {!c.approved && (
                            <span className="text-[10px] uppercase font-bold ml-2 px-1 rounded" style={{ background: "#FFF4E0", color: "#C58800" }}>
                              nem jóváhagyott
                            </span>
                          )}
                          <div className="text-xs font-mono" style={{ color: "var(--text-mute)" }}>
                            {c.name} · {c.email}{c.tax_id ? ` · ${c.tax_id}` : ""}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Alvázszám (VIN)</label>
            <input className="input" value={vin} onChange={(e) => setVin(e.target.value)}
              placeholder="Pl. WVWZZZ1JZXW000001" data-testid="new-order-vin" />
            <div className="mt-3">
              <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>
                Rendelés dátuma <span className="normal-case font-normal">(akár régebbi is)</span>
              </label>
              <input type="date" className="input" value={orderDate} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setOrderDate(e.target.value)} data-testid="new-order-date" />
            </div>
          </div>
        </div>

        {/* Product search */}
        <div className="relative">
          <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Tétel hozzáadása — cikkszám vagy megnevezés</label>
          <div className="flex items-center gap-2 border rounded px-3 py-2" style={{ borderColor: "var(--border-hi)" }}>
            <Search size={15} style={{ color: "var(--text-mute)" }} />
            <input
              className="flex-1 outline-none text-sm bg-transparent"
              placeholder="Pl. 06A115561B..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="new-order-search"
            />
            {searching && <span className="text-xs" style={{ color: "var(--text-mute)" }}>Keresés...</span>}
            {q && <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }}><X size={13} /></button>}
          </div>
          {results.length > 0 && (
            <div className="absolute left-0 right-0 z-30 bg-white border shadow-lg max-h-[300px] overflow-y-auto"
              style={{ borderColor: "var(--border-hi)" }} data-testid="new-order-results">
              {results.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 border-b hover:bg-slate-50"
                  style={{ borderColor: "var(--border)" }}>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-sm font-bold" style={{ color: "var(--brand)" }}>{p.sku}</span>
                    <div className="text-xs truncate" style={{ color: "var(--text-mute)" }}>{p.name}</div>
                  </div>
                  <button onClick={() => addLine(p)} className="btn-primary !py-1 !px-2.5 text-xs"
                    data-testid={`new-order-add-${p.sku}`}>
                    <Plus size={12} /> Hozzáad
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lines */}
        {lines.length > 0 && (
          <table className="table-b2b" style={{ border: "1px solid var(--border)" }} data-testid="new-order-lines">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Cikkszám</th>
                <th>Megnevezés</th>
                <th style={{ width: 120 }}>Db</th>
                <th style={{ width: 160 }}>Egyedi ár (EUR, opcionális)</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.product.id}>
                  <td><span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{l.product.sku}</span></td>
                  <td>{l.product.name}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button className="btn-ghost !py-0.5 !px-1.5 text-xs" disabled={l.quantity <= 1}
                        onClick={() => setLine(i, { quantity: l.quantity - 1 })}>−</button>
                      <input type="number" min={1} value={l.quantity}
                        onChange={(e) => setLine(i, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                        className="input !py-0.5 !px-1 text-center font-mono" style={{ width: 52 }}
                        data-testid={`new-order-qty-${l.product.sku}`} />
                      <button className="btn-ghost !py-0.5 !px-1.5 text-xs"
                        onClick={() => setLine(i, { quantity: l.quantity + 1 })}>+</button>
                    </div>
                  </td>
                  <td>
                    <input type="number" min="0.01" step="0.01" value={l.override}
                      onChange={(e) => setLine(i, { override: e.target.value })}
                      className="input !py-1 font-mono" placeholder="vevő árazása"
                      title="Ha üres, a vevő saját árazása érvényesül. Telefonon megbeszélt nettó EUR ár esetén írd be ide."
                      data-testid={`new-order-override-${l.product.sku}`} />
                  </td>
                  <td>
                    <button onClick={() => setLines((arr) => arr.filter((_, j) => j !== i))}
                      className="btn-danger !py-1 !px-2 text-xs" data-testid={`new-order-remove-${l.product.sku}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs" style={{ color: "var(--text-mute)" }}>
          Az árakat a rendszer a kiválasztott <strong>vevő saját árazása</strong> szerint számolja (árrés, akciók) — kivéve, ahol egyedi árat adsz meg.
        </p>

        <div>
          <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Megjegyzés</label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Pl. telefonos rendelés — visszahívás kérve..." data-testid="new-order-notes" />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
              data-testid="new-order-send-email" />
            Visszaigazoló e-mail küldése a vevőnek
          </label>
          <button onClick={save} className="btn-yellow" disabled={saving} data-testid="new-order-save">
            <Save size={14} /> {saving ? "Mentés..." : "Rendelés rögzítése"}
          </button>
        </div>
      </div>
    </div>
  );
}
